// Sale Highlights — the homepage sidebar's live feed of standout feeder lots
// (owner, 2026-10-14; migration 20261014000004). The database finds and posts
// them every minute; this file only reads and words them.
//
// ⚠️ EVERY NUMBER IS THE SERVER'S. The post is Rob's one-line format, filled in
// from the row: "Torrington Livestock Markets (Torrington, WY) reports 366
// Heifers (796 lbs) @ $340.00". Under it, only the week-over-week change
// (owner: "get rid of this bottom text except for the week over week change").
// $/cwt only.

// ⚠️ NO STATIC IMPORT OF cachedRpc (or the Supabase client). This file is in
// the homepage's server prerender, and the client touches localStorage when it
// loads — which Node 20 (Vercel, CI) does not have. It broke the first build of
// PR #2685; Node 26 on the dev machine has localStorage and hid it. Load both
// inside the functions, which only ever run in the browser.

export type HighlightKind = "BIG DRAFT" | "TOP PRICE" | "BIG GROUP" | "HEAVY";

export interface SaleHighlight {
  id: number;
  lotAt: string;
  saleDay: string;
  barn: string;
  city: string | null;
  state: string | null;
  cattleType: string;
  head: number;
  weight: number;
  price: number;
  premium: number;
  kind: HighlightKind;
  /** IANA zone of the barn, for the time on the post. */
  tz: string;
  /** Admin view only. */
  status?: string;
  statusReason?: string | null;
}

type Row = {
  id: number | string; lot_at: string; sale_day: string; barn: string; city: string | null; state: string | null;
  cattle_type: string; head_count: number | string; avg_weight: number | string; price_cwt: number | string;
  premium_cwt: number | string; kind: string; barn_tz: string | null; status?: string; status_reason?: string | null;
};

const KINDS: ReadonlySet<string> = new Set(["BIG DRAFT", "TOP PRICE", "BIG GROUP", "HEAVY"]);

/** PostgREST hands numerics back as strings; coerce every one. */
export function toHighlight(r: Row): SaleHighlight | null {
  const price = Number(r.price_cwt), premium = Number(r.premium_cwt), head = Number(r.head_count), weight = Number(r.avg_weight);
  if (!r.barn || !KINDS.has(r.kind) || ![price, premium, head, weight].every(Number.isFinite)) return null;
  return {
    id: Number(r.id), lotAt: r.lot_at, saleDay: r.sale_day, barn: r.barn, city: r.city, state: r.state,
    cattleType: r.cattle_type, head, weight: Math.round(weight), price, premium, kind: r.kind as HighlightKind,
    tz: r.barn_tz || "America/Chicago", status: r.status, statusReason: r.status_reason ?? null,
  };
}

/** A post this young carries the NEW tag and the gold edge (owner, 2026-10-14:
 *  "Can we somehow highlight new highlights that are coming in?"). */
export const NEW_FOR_MS = 10 * 60_000;
export function isNew(lotAt: string, now: number): boolean {
  const t = Date.parse(lotAt);
  return Number.isFinite(t) && now - t >= 0 && now - t < NEW_FOR_MS;
}

export const money = (n: number) => `$${n.toFixed(2)}`;

/** "Torrington Livestock Markets (Torrington, WY)" */
export function barnLabel(h: SaleHighlight): string {
  const where = [h.city, h.state].filter(Boolean).join(", ");
  return where ? `${h.barn} (${where})` : h.barn;
}

/** "reports 366 Heifers (796 lbs) @ $340.00" */
export function reportText(h: SaleHighlight): string {
  return `reports ${h.head} ${h.cattleType} (${h.weight} lbs) @ ${money(h.price)}`;
}

/** "+$13.77" — the sign always shows. */
export function signedMoney(n: number): string {
  return `${n >= 0 ? "+" : "−"}${money(Math.abs(n))}`;
}

/** "+$13.77 vs last week" */
export function vsLastWeek(premium: number): string {
  return `${signedMoney(premium)} vs last week`;
}

/** "10/14 12:13 PM CDT", on the barn's own clock. */
export function postTime(h: SaleHighlight): string {
  const d = new Date(h.lotAt);
  if (Number.isNaN(d.getTime())) return "";
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: h.tz, month: "2-digit", day: "2-digit", hour: "numeric", minute: "2-digit", timeZoneName: "short",
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
    return `${get("month")}/${get("day")} ${get("hour")}:${get("minute")} ${get("dayPeriod")} ${get("timeZoneName")}`.trim();
  } catch {
    return "";
  }
}

/** The public feed — CDN-cached through /api/market. */
export async function fetchSaleHighlights(): Promise<SaleHighlight[] | null> {
  // No params: one cache key for every visitor (the RPC's default is 40 posts).
  const { cachedRpc } = await import("@/lib/cachedRpc");
  const { data, error } = await cachedRpc<Row[]>("rb_sale_highlights_feed");
  if (error || !Array.isArray(data)) return null;
  return data.map(toHighlight).filter((h): h is SaleHighlight => h !== null);
}

/** Rob and the owner: every post from the last two days, held and hidden included. */
export async function fetchAdminHighlights(): Promise<SaleHighlight[] | null> {
  const { supabase } = await import("@/integrations/supabase/client");
  const { data, error } = await supabase.rpc("admin_rb_sale_highlights" as never, { p_days: 2 } as never);
  if (error || !Array.isArray(data)) return null;
  return (data as Row[]).map(toHighlight).filter((h): h is SaleHighlight => h !== null);
}

/** Hide a post, or post a held/hidden one. */
export async function setHighlightStatus(id: number, status: "live" | "hidden"): Promise<string | null> {
  const { supabase } = await import("@/integrations/supabase/client");
  const { error } = await supabase.rpc("admin_rb_sale_highlight_set" as never, { p_id: id, p_status: status } as never);
  return error ? (error as { message?: string }).message ?? "Could not save" : null;
}
